import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg, { getWebPageSchema } from '@/components/feature/SchemaOrg';
import { movies } from '@/mocks/movies';
import { Helmet } from '@/pages/home/components/SEO';

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <Helmet
          title="影片未找到"
          description="抱歉，您查找的影片不存在。浏览被窝网电影大全发现更多精彩影视资源。"
          keywords="被窝网电影,电影大全,免费电影"
          canonicalUrl="/"
        />
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-3xl" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">
              影片未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              抱歉，您查找的影片不存在或已被移除
            </p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 5);

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    image: movie.cover,
    dateCreated: String(movie.year),
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((actor) => ({
      '@type': 'Person',
      name: actor,
    })),
    description: movie.description,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(movie.rating),
      bestRating: '10',
      worstRating: '0',
    },
    genre: [movie.category],
  };

  return (
    <>
      <Helmet
        title={`${movie.title} - 被窝网电影免费高清在线观看`}
        description={`${movie.title}(${movie.year})${movie.description.slice(0, 100)}... 被窝网电影为您提供${movie.title}免费高清在线观看，播放不缓冲，流畅观影体验。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}免费,${movie.category},被窝网电影,高清电影`}
        canonicalUrl={`/movie/${movie.id}`}
        ogType="article"
        ogImage={movie.cover}
      />
      <SchemaOrg schema={movieSchema} />
      <SchemaOrg schema={getWebPageSchema(
        `${movie.title} - 被窝网电影免费高清在线观看`,
        `${movie.title}(${movie.year})${movie.description.slice(0, 80)}...`,
        `/movie/${movie.id}`,
        [
          { name: '首页', url: '/' },
          { name: movie.category, url: `/category/${movie.categorySlug}` },
          { name: movie.title, url: `/movie/${movie.id}` },
        ]
      )} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: movie.category, href: `/category/${movie.categorySlug}` },
              { label: movie.title },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="w-full lg:w-[300px] shrink-0">
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={movie.cover}
                      alt={`${movie.title}海报`}
                      title={`${movie.title} - 被窝网电影高清在线观看`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-semibold bg-primary-500 text-background-50">
                        <i className="ri-star-fill" />
                        {movie.rating}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">{movie.titleEn}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                      {movie.category}
                    </span>
                    {movie.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex px-2.5 py-1 rounded-md text-xs font-medium bg-background-100 text-foreground-600 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6 p-5 rounded-lg bg-background-100">
                    <div>
                      <span className="text-xs text-foreground-400">年份</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.year}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">地区</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.region}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">片长/集数</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">
                        {movie.duration}
                        {movie.type === 'tv' && ` / 共${movie.episodes}集`}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">导演</span>
                      <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.director}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">评分</span>
                      <p className="text-sm font-medium text-accent-600 mt-0.5">{movie.rating} 分</p>
                    </div>
                    {movie.type === 'tv' && movie.updateStatus && (
                      <div>
                        <span className="text-xs text-foreground-400">状态</span>
                        <p className="text-sm font-medium text-primary-500 mt-0.5">{movie.updateStatus}</p>
                      </div>
                    )}
                  </div>

                  <div className="mb-6">
                    <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      剧情简介
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {movie.description}
                    </p>
                  </div>

                  <div>
                    <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      主演
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {movie.cast.map((actor) => (
                        <span
                          key={actor}
                          className="inline-flex px-3 py-1.5 rounded-md text-xs font-medium bg-background-100 text-foreground-700 whitespace-nowrap"
                        >
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}